import { useEffect, useRef, useCallback } from 'react';

export type CanvasSyncEventType =
  | 'node-added'
  | 'node-updated'
  | 'node-deleted'
  | 'edge-added'
  | 'edge-deleted'
  | 'node-moved'
  | 'node-dragging';

export interface CanvasSyncEvent {
  type: CanvasSyncEventType;
  payload: any;
  senderId?: string;
}

const RECONNECT_DELAY = 3000;
const MAX_RECONNECT_DELAY = 30000;

// Module-level clientId so canvasService can access it
let currentClientId: string | null = null;
export function getCanvasSyncClientId(): string | null {
  return currentClientId;
}

export function useCanvasSync(canvasId: string, onEvent: (event: CanvasSyncEvent) => void, userId?: string, userName?: string) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onEventRef = useRef(onEvent);

  // Keep callback ref up to date without re-connecting
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const connect = useCallback(() => {
    if (!canvasId) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    let wsUrl = `${protocol}//${window.location.host}/ws/canvas?canvasId=${encodeURIComponent(canvasId)}`;
    if (userId) wsUrl += `&userId=${encodeURIComponent(userId)}`;
    if (userName) wsUrl += `&userName=${encodeURIComponent(userName)}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectAttemptRef.current = 0;
      console.log('[CanvasSync] Connected');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'connected') {
          currentClientId = data.payload?.clientId || null;
          console.log('[CanvasSync] Assigned clientId:', currentClientId);
          return;
        }
        onEventRef.current(data as CanvasSyncEvent);
      } catch (err) {
        console.error('[CanvasSync] Failed to parse event:', err);
      }
    };

    ws.onclose = (event) => {
      console.log('[CanvasSync] Disconnected', event.code);
      if (wsRef.current === ws) {
        // Schedule reconnect with exponential backoff
        const delay = Math.min(
          RECONNECT_DELAY * Math.pow(2, reconnectAttemptRef.current),
          MAX_RECONNECT_DELAY
        );
        reconnectAttemptRef.current++;
        reconnectTimeoutRef.current = setTimeout(() => {
          if (wsRef.current === ws || wsRef.current === null) {
            connect();
          }
        }, delay);
      }
    };

    ws.onerror = (err) => {
      console.error('[CanvasSync] WebSocket error:', err);
    };
  }, [canvasId, userId, userName]);

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return wsRef;
}
